import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { contactAPI } from "../../services/contactAPI"

// Async thunk for sending contact message
export const sendContactMessage = createAsyncThunk(
  "contact/sendMessage",
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await contactAPI.createContact(messageData)
      return response
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

// Async thunk to fetch all contacts
export const getAllContactsThunk = createAsyncThunk(
  "contact/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await contactAPI.getAllContacts()
      return response
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch contacts")
    }
  },
)

// ✅ Async thunk to delete a contact
export const deleteContactThunk = createAsyncThunk(
  "contact/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await contactAPI.deleteContact(id)
      return response // can return deleted contact
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete contact")
    }
  },
)

const contactSlice = createSlice({
  name: "contact",
  initialState: {
    loading: false,
    success: false,
    error: null,
    message: "",
    contacts: [],
    deleting: false, // ✅ track deleting state
  },
  reducers: {
    resetContactState: (state) => {
      state.loading = false
      state.success = false
      state.error = null
      state.message = ""
      state.deleting = false
    },
  },
  extraReducers: (builder) => {
    // sendContactMessage
    builder
      .addCase(sendContactMessage.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
        state.message = ""
      })
      .addCase(sendContactMessage.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.message = action.payload.message
      })
      .addCase(sendContactMessage.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || "Failed to send message"
      })

    // getAllContactsThunk
    builder
      .addCase(getAllContactsThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllContactsThunk.fulfilled, (state, action) => {
        state.loading = false
        state.contacts = action.payload
      })
      .addCase(getAllContactsThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to fetch contacts"
      })

    // deleteContactThunk
    builder
      .addCase(deleteContactThunk.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteContactThunk.fulfilled, (state, action) => {
        state.deleting = false
        // Remove deleted contact from state.contacts
        state.contacts = state.contacts.filter(
          (contact) => contact.id !== action.payload.id,
        )
      })
      .addCase(deleteContactThunk.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload || "Failed to delete contact"
      })
  },
})

export const { resetContactState } = contactSlice.actions
export const selectContact = (state) => state.contact
export default contactSlice.reducer
